import { cn } from "../../../utils/cn.js";
import { ScrollArea, ScrollAreaContent, ScrollAreaCorner, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport } from "../ScrollArea/ScrollArea.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { Button } from "../Button/Button.js";
import { Checkbox } from "../Checkbox/Checkbox.js";
import { EmptyState, EmptyStateDescription, EmptyStateIcon, EmptyStateTitle } from "../EmptyState/EmptyState.js";
import { Select } from "../Select/Select.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./Table.js";
import { cva } from "class-variance-authority";
import { ArrowDownIcon, ArrowUpIcon, CaretDoubleLeftIcon, CaretDoubleRightIcon, CaretLeftIcon, CaretRightIcon, CaretUpDownIcon, ListDashesIcon } from "@phosphor-icons/react";
import * as React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { flexRender } from "@tanstack/react-table";
//#region src/components/Table/DataTable.tsx
/**
* Class variants for pinned (sticky) table cells.
*
* Each left-pinned cell gets a right border; each right-pinned cell gets a
* left border so pinned groups read as distinct from the scroll region.
*
* A horizontal edge fade is applied via {@link getPinnedEdgeClassNames} on the
* outermost pinned column only (`::after` linear gradient on the scroll seam, not
* per-cell `box-shadow`). Does not set `position: relative` so `sticky` is preserved.
* Fade depth scales with `--data-table-overflow-x-start` / `-end`, synced from
* the scroll viewport inside {@link DataTable} (Base UI overflow vars do not
* inherit to table cells).
*
* NOTE: The parent `<table>` must use `border-separate border-spacing-0`
* instead of `border-collapse` so Chrome does not clip overflow from sticky
* cells.
*/
const pinnedCellVariants = cva("", {
	compoundVariants: [
		{
			cellType: "header",
			pinned: "left",
			className: "bg-elevation-surface-primary"
		},
		{
			cellType: "header",
			pinned: "right",
			className: "bg-elevation-surface-primary"
		},
		{
			cellType: "body",
			pinned: "left",
			className: "bg-elevation-surface-primary"
		},
		{
			cellType: "body",
			pinned: "right",
			className: "bg-elevation-surface-primary"
		}
	],
	defaultVariants: {
		cellType: "body",
		pinned: false
	},
	variants: {
		cellType: {
			body: "",
			header: ""
		},
		pinned: {
			false: "",
			left: "sticky z-[1] border-r border-r-(length:--border-width-default) border-r-border-primary",
			right: "sticky z-[1] border-l border-l-(length:--border-width-default) border-l-border-primary"
		}
	}
});
/** Max width of the pinned ↔ scrollable seam fade (scales via `--data-table-overflow-x-*`). */
const PINNED_EDGE_FADE_WIDTH_CLASS = "after:[width:min(var(--data-table-pinned-edge-fade-size,12px),var(--data-table-overflow-x-start,0px))]";
const PINNED_EDGE_FADE_WIDTH_RIGHT_CLASS = "after:[width:min(var(--data-table-pinned-edge-fade-size,12px),var(--data-table-overflow-x-end,0px))]";
const DEFAULT_PINNED_EDGE_FADE_SIZE_PX = 12;
/**
* Parses a CSS length value as pixels for overflow distance fallbacks.
*
* @param value - Raw value from `getComputedStyle` (e.g. `"12px"`)
* @returns Pixel length, or 0 when unparseable
*/
function parseCssLengthPx(value) {
	const trimmed = value.trim();
	if (!trimmed) return 0;
	const px = Number.parseFloat(trimmed);
	return Number.isFinite(px) ? px : 0;
}
/**
* Mirrors horizontal scroll overflow distances onto the table wrapper so pinned
* seam fades can read `--data-table-overflow-x-*` (Base UI sets those vars on the
* viewport with `inherits: false`). Falls back to `scrollLeft` when the vars are
* zero (e.g. overlay scrollbars on macOS).
*
* @param viewportRef - ScrollArea viewport element
* @param contentRef - Wrapper around table content that receives the CSS variables
* @param enabled - When false, the effect is not installed
*/
function useDataTablePinnedOverflowSync(viewportRef, contentRef, enabled) {
	React.useLayoutEffect(() => {
		if (!enabled) return;
		const viewport = viewportRef.current;
		const content = contentRef.current;
		if (!viewport || !content) return;
		const sync = () => {
			const fadeSizePx = parseCssLengthPx(getComputedStyle(content).getPropertyValue("--data-table-pinned-edge-fade-size")) || DEFAULT_PINNED_EDGE_FADE_SIZE_PX;
			const viewportStyles = getComputedStyle(viewport);
			const scrollFromEndX = viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft;
			const xStartPx = Math.max(parseCssLengthPx(viewportStyles.getPropertyValue("--scroll-area-overflow-x-start")), Math.min(fadeSizePx, viewport.scrollLeft));
			const xEndPx = Math.max(parseCssLengthPx(viewportStyles.getPropertyValue("--scroll-area-overflow-x-end")), Math.min(fadeSizePx, scrollFromEndX));
			content.style.setProperty("--data-table-overflow-x-start", `${xStartPx}px`);
			content.style.setProperty("--data-table-overflow-x-end", `${xEndPx}px`);
		};
		sync();
		viewport.addEventListener("scroll", sync, { passive: true });
		const resizeObserver = new ResizeObserver(sync);
		resizeObserver.observe(viewport);
		return () => {
			viewport.removeEventListener("scroll", sync);
			resizeObserver.disconnect();
		};
	}, [
		contentRef,
		enabled,
		viewportRef
	]);
}
/**
* Seam fade on the outermost pinned column cells.
*
* Renders a full-row `::after` strip with a horizontal linear gradient on the
* scroll seam only (y-offset zero — no radial per-cell box-shadow). Sticky cells
* are the containing block; do not add `position: relative` here.
*
* @param column - TanStack column instance for the cell being rendered
* @param table - Table instance (visible leaf order)
* @param disableShadow - When true, omits the edge fade for this column
* @returns Additional class names for outer pinned edge cells
*/
function getPinnedEdgeClassNames(column, table, disableShadow) {
	if (disableShadow) return "";
	const pin = column.getIsPinned();
	if (!pin) return "";
	const leaf = table.getVisibleLeafColumns();
	const leftPinned = leaf.filter((c) => c.getIsPinned() === "left");
	const rightPinned = leaf.filter((c) => c.getIsPinned() === "right");
	const isLastLeft = pin === "left" && leftPinned.length > 0 && leftPinned[leftPinned.length - 1]?.id === column.id;
	const isFirstRight = pin === "right" && rightPinned.length > 0 && rightPinned[0]?.id === column.id;
	if (!isLastLeft && !isFirstRight) return "";
	return cn(cn("overflow-visible", "after:pointer-events-none after:absolute after:top-0 after:bottom-0 after:z-[2] after:content-['']"), isLastLeft && [
		"after:left-full",
		PINNED_EDGE_FADE_WIDTH_CLASS,
		"after:bg-gradient-to-r after:from-[rgba(9,30,66,0.12)] after:to-transparent",
		"dark:after:from-[rgba(3,4,4,0.28)] dark:after:to-transparent"
	], isFirstRight && [
		"after:right-full",
		PINNED_EDGE_FADE_WIDTH_RIGHT_CLASS,
		"after:bg-gradient-to-l after:from-[rgba(9,30,66,0.12)] after:to-transparent",
		"dark:after:from-[rgba(3,4,4,0.28)] dark:after:to-transparent"
	]);
}
/**
* Returns inline styles for a column based on its declared sizing options.
*
* Only columns that explicitly set `size` are width-locked
* (`width` + `minWidth` + `maxWidth`). Columns that only provide `minSize`
* or `maxSize` keep those as flexible bounds so they can shrink and grow
* naturally within the table.
*
* Pinned columns also receive the sticky `left` / `right` offset.
*/
function getColumnStyles(column) {
	const pin = column.getIsPinned();
	const { maxSize, minSize, size } = column.columnDef;
	const widthStyles = size !== void 0 ? {
		maxWidth: maxSize ?? size,
		minWidth: minSize ?? size,
		width: size
	} : minSize !== void 0 || maxSize !== void 0 ? {
		maxWidth: maxSize,
		minWidth: minSize
	} : void 0;
	if (pin) return {
		left: pin === "left" ? `${column.getStart("left")}px` : void 0,
		right: pin === "right" ? `${column.getAfter("right")}px` : void 0,
		...widthStyles
	};
	return widthStyles;
}
/**
* Creates a reusable checkbox selection column for TanStack React Table.
*
* The header renders an indeterminate-aware checkbox that toggles all rows
* on the current page. Each body cell renders a checkbox bound to that row's
* selection state. Click events are stopped from propagating so row-click
* handlers (e.g. "open detail") are not triggered by selection clicks.
*
* The consumer still owns all selection state — configure `enableRowSelection`,
* `state.rowSelection`, and `onRowSelectionChange` on the `useReactTable` call.
* Add `'select'` to `columnPinning.left` for sticky behavior.
*
* @returns A `ColumnDef` that renders selection checkboxes
*
* @example
* ```tsx
* const columns: ColumnDef<Order>[] = [
*   createSelectionColumn<Order>(),
*   { accessorKey: 'id', header: 'ID' },
* ]
* ```
*/
function createSelectionColumn() {
	return {
		cell: ({ row }) => /* @__PURE__ */ jsx("div", {
			className: "flex items-center justify-center",
			onClick: (e) => e.stopPropagation(),
			children: /* @__PURE__ */ jsx(Checkbox, {
				checked: row.getIsSelected(),
				disabled: !row.getCanSelect(),
				indeterminate: row.getIsSomeSelected(),
				onCheckedChange: (value) => row.toggleSelected(Boolean(value)),
				size: "sm"
			})
		}),
		enableHiding: false,
		enablePinning: true,
		enableSorting: false,
		header: ({ table }) => /* @__PURE__ */ jsx("div", {
			className: "flex items-center justify-center",
			onClick: (e) => e.stopPropagation(),
			children: /* @__PURE__ */ jsx(Checkbox, {
				checked: table.getIsAllPageRowsSelected(),
				indeterminate: table.getIsSomePageRowsSelected(),
				onCheckedChange: (value) => table.toggleAllPageRowsSelected(Boolean(value)),
				size: "sm"
			})
		}),
		id: "select",
		meta: { cellClassName: "!px-0" },
		size: 40
	};
}
const DEFAULT_EMPTY_STATE = /* @__PURE__ */ jsxs(EmptyState, { children: [
	/* @__PURE__ */ jsx(EmptyStateIcon, { children: /* @__PURE__ */ jsx(ListDashesIcon, {}) }),
	/* @__PURE__ */ jsx(EmptyStateTitle, { children: "No results" }),
	/* @__PURE__ */ jsx(EmptyStateDescription, { children: "No matching records were found." })
] });
const dataTableVariants = cva("relative min-w-0 max-w-full w-full overflow-hidden font-body", {
	defaultVariants: { variant: "default" },
	variants: { variant: {
		default: "rounded-sm border border-(length:--border-width-default) border-border-primary [--data-table-pinned-edge-fade-size:12px] [--scroll-fade-color:var(--color-background-neutral-primary)]",
		flat: "[--data-table-pinned-edge-fade-size:12px] [--scroll-fade-color:var(--color-elevation-surface-primary)]"
	} }
});
const DataTableSubRowContext = React.createContext(null);
const SUB_ROW_FALLBACK_MS = 400;
/**
* Either property ending means the close is visually done. `grid-template-rows`
* does not fire `transitionend` inside a table cell in every browser, and the
* opacity fade is what makes the row look closed; waiting on the row-size
* event alone leaves the empty row mounted until the fallback timer.
*/
const SUB_ROW_COLLAPSE_END_PROPERTIES = /* @__PURE__ */ new Set(["grid-template-rows", "opacity"]);
/**
* Keeps a sub-row rendered while its parent collapses.
*
* `row.getIsExpanded()` flips false in the same render that starts the
* collapse, so a sub-row gated on it alone unmounts before any exit
* animation can play. Rows that were expanded on the previous render and are
* not now are held in a closing set, and `renderSubComponent` keeps running
* for them until `DataTableSubRowPanel` reports the transition finished.
*
* The bookkeeping happens during render (not in an effect) so the frame that
* collapses a row still contains its sub-row; an effect would drop it for a
* frame and then remount it, replaying the enter animation and any lazy
* fetch inside. The ref writes are idempotent set operations, so a repeated
* render (StrictMode) lands in the same state.
*
* @param rows - Current row model
* @returns Whether to render a given row's sub-row, and the completion callback
*/
function useClosingSubRows(rows) {
	const previouslyExpanded = React.useRef(/* @__PURE__ */ new Set());
	const closing = React.useRef(/* @__PURE__ */ new Set());
	const [, bump] = React.useReducer((count) => count + 1, 0);
	const expandedNow = /* @__PURE__ */ new Set();
	const presentIds = /* @__PURE__ */ new Set();
	for (const row of rows) {
		presentIds.add(row.id);
		if (row.getIsExpanded()) {
			expandedNow.add(row.id);
			closing.current.delete(row.id);
		} else if (previouslyExpanded.current.has(row.id)) closing.current.add(row.id);
	}
	for (const id of closing.current) if (!presentIds.has(id)) closing.current.delete(id);
	previouslyExpanded.current = expandedNow;
	return {
		shouldRenderSubRow: (row) => row.getIsExpanded() || closing.current.has(row.id),
		finishClosing: React.useCallback((rowId) => {
			if (closing.current.delete(rowId)) bump();
		}, [])
	};
}
/**
* Animated wrapper for sub-row content rendered via `renderSubComponent`.
*
* A `<tr>` cannot animate its own height, so this sits inside the sub-row's
* full-width cell and animates `grid-template-rows` between `0fr` and `1fr`
* (the same technique as `CollapsiblePanel`). It opens from zero on mount via
* `@starting-style` and closes when the parent row is no longer expanded,
* then tells the table the collapse is done so the row can unmount. The
* content wrapper clips with `overflow: clip` rather than `hidden` so
* `position: sticky` descendants keep tracking the table's scrollport.
*
* Outside a `DataTable` sub-row it renders open and static.
*
* @param props - Standard div props applied to the content inside the clip
* @returns The rendered animated panel
*/
function DataTableSubRowPanel({ className, children, ...props }) {
	const state = React.useContext(DataTableSubRowContext);
	const expanded = state?.expanded ?? true;
	const onCollapseComplete = state?.onCollapseComplete;
	const onCollapseCompleteRef = React.useRef(onCollapseComplete);
	onCollapseCompleteRef.current = onCollapseComplete;
	React.useEffect(() => {
		if (expanded) return;
		const timer = setTimeout(() => {
			onCollapseCompleteRef.current?.();
		}, SUB_ROW_FALLBACK_MS);
		return () => clearTimeout(timer);
	}, [expanded]);
	return /* @__PURE__ */ jsx("div", {
		className: cn("grid", "[transition-property:grid-template-rows,opacity] duration-[280ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]", "motion-reduce:transition-none", expanded ? "grid-rows-[1fr] opacity-100 starting:grid-rows-[0fr] starting:opacity-0" : "grid-rows-[0fr] opacity-0"),
		"data-state": expanded ? "open" : "closed",
		onTransitionEnd: (event) => {
			if (!expanded && event.target === event.currentTarget && SUB_ROW_COLLAPSE_END_PROPERTIES.has(event.propertyName)) onCollapseCompleteRef.current?.();
		},
		children: /* @__PURE__ */ jsx("div", {
			className: "min-h-0 overflow-clip",
			children: /* @__PURE__ */ jsx("div", {
				className,
				...props,
				children
			})
		})
	});
}
/** Cap on loading placeholder rows; more than this would not fit on screen. */
const MAX_SKELETON_ROWS = 20;
/**
* Focus ring for a clickable row, drawn on its cells: pinned cells are
* positioned above the row, so an outline on the `<tr>` would hide under them.
*/
const FOCUSABLE_ROW_CLASSES = [
	"outline-none",
	"focus-visible:[&>td]:shadow-[inset_0_2px_0_var(--color-text-selected),inset_0_-2px_0_var(--color-text-selected)]",
	"focus-visible:[&>td:first-child]:shadow-[inset_2px_0_0_var(--color-text-selected),inset_0_2px_0_var(--color-text-selected),inset_0_-2px_0_var(--color-text-selected)]",
	"focus-visible:[&>td:last-child]:shadow-[inset_-2px_0_0_var(--color-text-selected),inset_0_2px_0_var(--color-text-selected),inset_0_-2px_0_var(--color-text-selected)]"
];
/**
* Enter or Space pressed on the row itself. A key pressed inside a control
* in the row (or in a menu it portals) targets that control, so it is left
* to the control and the row does not activate a second time.
*/
function isRowActivationKey(event) {
	return (event.key === "Enter" || event.key === " ") && !event.repeat && event.target === event.currentTarget;
}
/**
* Loading placeholder rows: one pulsing bar per visible column, laid out with
* the same pinned-column treatment as data cells so the table does not shift
* when rows arrive. Hidden from assistive tech; {@link DataTable} announces
* the loading state separately.
*/
function DataTableSkeletonRows({ table, rowCount }) {
	const columns = table.getVisibleLeafColumns();
	return /* @__PURE__ */ jsx(TableBody, {
		"aria-hidden": true,
		"data-slot": "data-table-skeleton",
		children: Array.from({ length: rowCount }, (_, index) => /* @__PURE__ */ jsx(TableRow, {
			className: "hover:not-data-[state=selected]:bg-transparent",
			children: columns.map((column) => {
				const meta = column.columnDef.meta;
				return /* @__PURE__ */ jsx(TableCell, {
					className: cn(pinnedCellVariants({
						cellType: "body",
						pinned: column.getIsPinned() || false
					}), getPinnedEdgeClassNames(column, table, meta?.disablePinnedEdgeShadow), meta?.cellClassName),
					style: getColumnStyles(column),
					children: /* @__PURE__ */ jsx(Skeleton, {
						radius: "xs",
						className: "h-[12px] w-full min-w-[24px] max-w-[160px]"
					})
				}, column.id);
			})
		}, index))
	});
}
/**
* Renders a full table from a TanStack React Table instance using Curri
* styled primitives.
*
* Designed for **remote / server-side data**: the consumer creates the
* table with `manualPagination`, `manualSorting`, etc. and controls all
* state externally. This component is purely presentational — it reads the
* table instance to render headers, rows, pinned columns, loading skeletons,
* and empty states.
*
* @param props - DataTable configuration and the TanStack table instance
* @returns The rendered data table
*/
function DataTable({ table, chainVerticalScroll = false, className, loading = false, emptyState = DEFAULT_EMPTY_STATE, stickyHeader = false, variant = "default", onRowClick, getRowClassName, renderSubComponent }) {
	const viewportRef = React.useRef(null);
	const scrollContentRef = React.useRef(null);
	useDataTablePinnedOverflowSync(viewportRef, scrollContentRef, table.getIsSomeColumnsPinned("left") || table.getIsSomeColumnsPinned("right"));
	const rows = table.getRowModel().rows;
	const rowCount = rows.length;
	const showLoadingState = loading;
	const showEmptyState = !loading && rowCount === 0;
	const { shouldRenderSubRow, finishClosing } = useClosingSubRows(rows);
	return /* @__PURE__ */ jsxs(ScrollArea, {
		className: cn(dataTableVariants({ variant }), className),
		children: [
			/* @__PURE__ */ jsx(ScrollAreaViewport, {
				ref: viewportRef,
				chainVerticalScroll,
				className: cn("relative h-full w-full", variant === "default" && "rounded-[inherit]"),
				scrollFade: true,
				children: /* @__PURE__ */ jsxs("div", {
					ref: scrollContentRef,
					className: cn("min-h-full", showEmptyState && "flex flex-col"),
					children: [
						stickyHeader ? /* @__PURE__ */ jsx("div", {
							"aria-hidden": true,
							className: "pointer-events-none sticky top-0 start-0 z-[1] block w-full shrink-0 bg-background-neutral-primary",
							style: {
								height: 28,
								marginBottom: -28
							}
						}) : null,
						/* @__PURE__ */ jsx(ScrollAreaContent, { children: /* @__PURE__ */ jsxs(Table, { children: [
							/* @__PURE__ */ jsx(TableHeader, {
								className: stickyHeader ? "sticky top-0 z-[2] bg-background-neutral-primary" : void 0,
								children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx(TableRow, { children: headerGroup.headers.map((header) => {
									const pinned = header.column.getIsPinned() || false;
									const meta = header.column.columnDef.meta;
									return /* @__PURE__ */ jsx(TableHead, {
										colSpan: header.colSpan,
										className: cn(pinnedCellVariants({
											cellType: "header",
											pinned
										}), getPinnedEdgeClassNames(header.column, table, meta?.disablePinnedEdgeShadow), meta?.cellClassName),
										style: getColumnStyles(header.column),
										children: header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())
									}, header.id);
								}) }, headerGroup.id))
							}),
							showLoadingState && /* @__PURE__ */ jsx(DataTableSkeletonRows, {
								table,
								rowCount: Math.min(table.getState().pagination.pageSize, MAX_SKELETON_ROWS)
							}),
							!showLoadingState && !showEmptyState && /* @__PURE__ */ jsx(TableBody, { children: rows.map((row) => /* @__PURE__ */ jsxs(React.Fragment, { children: [/* @__PURE__ */ jsx(TableRow, {
								"data-state": row.getIsSelected() ? "selected" : void 0,
								className: cn(onRowClick && ["cursor-pointer", FOCUSABLE_ROW_CLASSES], getRowClassName?.(row)),
								tabIndex: onRowClick ? 0 : void 0,
								onClick: onRowClick ? (e) => onRowClick(row, e) : void 0,
								onKeyDown: onRowClick ? (e) => {
									if (!isRowActivationKey(e)) return;
									e.preventDefault();
									onRowClick(row, e);
								} : void 0,
								children: row.getVisibleCells().map((cell) => {
									const meta = cell.column.columnDef.meta;
									return /* @__PURE__ */ jsx(TableCell, {
										className: cn(pinnedCellVariants({
											cellType: "body",
											pinned: cell.column.getIsPinned() || false
										}), getPinnedEdgeClassNames(cell.column, table, meta?.disablePinnedEdgeShadow), meta?.cellClassName),
										style: getColumnStyles(cell.column),
										children: flexRender(cell.column.columnDef.cell, cell.getContext())
									}, cell.id);
								})
							}), renderSubComponent && shouldRenderSubRow(row) && /* @__PURE__ */ jsx(DataTableSubRowContext.Provider, {
								value: {
									expanded: row.getIsExpanded(),
									onCollapseComplete: () => finishClosing(row.id)
								},
								children: renderSubComponent(row)
							})] }, row.id)) })
						] }) }),
						showLoadingState && /* @__PURE__ */ jsx("span", {
							role: "status",
							"aria-label": "Loading",
							className: "sr-only",
							children: "Loading"
						}),
						showEmptyState && /* @__PURE__ */ jsx("div", {
							className: "sticky left-0 flex w-full flex-1 items-center justify-center px-lg py-xl",
							children: emptyState
						})
					]
				})
			}),
			/* @__PURE__ */ jsx(ScrollAreaScrollbar, {
				className: "z-[3]",
				orientation: "vertical",
				children: /* @__PURE__ */ jsx(ScrollAreaThumb, {})
			}),
			/* @__PURE__ */ jsx(ScrollAreaScrollbar, {
				className: "z-[3]",
				orientation: "horizontal",
				children: /* @__PURE__ */ jsx(ScrollAreaThumb, {})
			}),
			/* @__PURE__ */ jsx(ScrollAreaCorner, {})
		]
	});
}
/**
* Column header with sort-direction indicators for use inside TanStack
* column definitions.
*
* When the column supports sorting, renders a clickable button that cycles
* through ascending → descending → unsorted states. Displays an arrow icon
* matching the current direction, or a neutral caret icon when unsorted.
* For non-sortable columns, renders a plain `<span>`.
*
* Designed for remote sorting — clicking the header updates TanStack's sort
* state, which the consumer propagates to the server via `onSortingChange`.
*
* @param props - Column instance, title label, and optional class overrides
* @returns The rendered column header
*
* @example
* ```tsx
* const columns: ColumnDef<User>[] = [
*   {
*     accessorKey: 'name',
*     header: ({ column }) => (
*       <DataTableColumnHeader column={column} title="Name" />
*     ),
*   },
* ]
* ```
*/
function DataTableColumnHeader({ column, title, className }) {
	if (!column.getCanSort()) return /* @__PURE__ */ jsx("span", {
		className,
		children: title
	});
	const sorted = column.getIsSorted();
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		className: cn("inline-flex items-center gap-xxs", "cursor-pointer border-0 bg-transparent p-0", "text-body-small leading-body-small font-heading-medium text-text-secondary", "hover:text-text-primary", className),
		onClick: () => {
			if (!sorted) column.toggleSorting(false);
			else if (sorted === "asc") column.toggleSorting(true);
			else column.clearSorting();
		},
		children: [title, sorted === "asc" ? /* @__PURE__ */ jsx(ArrowUpIcon, { style: {
			width: 8,
			height: 8
		} }) : sorted === "desc" ? /* @__PURE__ */ jsx(ArrowDownIcon, { style: {
			width: 8,
			height: 8
		} }) : /* @__PURE__ */ jsx(CaretUpDownIcon, { style: {
			width: 8,
			height: 8,
			opacity: .5
		} })]
	});
}
/**
* Pagination controls for a TanStack-powered DataTable.
*
* Displays a rows-per-page selector, a "Page X of Y" indicator, and
* first / previous / next / last navigation buttons. All state changes
* flow through the table instance, so the consumer's `onPaginationChange`
* callback is invoked automatically for server-side fetching.
*
* @param props - Table instance, page size options, and class overrides
* @returns The rendered pagination bar
*
* @example
* ```tsx
* <DataTable table={table} footer={<DataTablePagination table={table} />} />
* ```
*/
function DataTablePagination({ table, pageSizeOptions = [
	10,
	25,
	50,
	100
], itemLabel, showLastPageButton = true, variant = "default", className }) {
	const { pageIndex, pageSize } = table.getState().pagination;
	const rowCount = table.getRowCount();
	const bar = variant === "bar";
	const selectOptions = React.useMemo(() => pageSizeOptions.map((size) => ({
		label: String(size),
		value: String(size)
	})), [pageSizeOptions]);
	const countLabel = itemLabel ? rowCount === 1 ? itemLabel.singular : itemLabel.plural : "row(s)";
	const textClass = bar ? "whitespace-nowrap" : "text-body-small leading-body-small font-heading-medium text-text-secondary whitespace-nowrap";
	const navButton = (label, disabled, onClick, icon) => /* @__PURE__ */ jsx(Button, {
		"aria-label": label,
		className: bar ? "border-0 text-text-secondary" : void 0,
		disabled,
		onClick,
		shape: "icon",
		size: bar ? "small" : "base",
		variant: bar ? "tertiary" : "secondary",
		children: icon
	});
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex items-center justify-between font-body", bar && "h-11 shrink-0 border-t border-t-(length:--border-width-default) border-t-border-primary px-lg text-body-base leading-body-base text-text-secondary", className),
		children: [/* @__PURE__ */ jsxs("span", {
			className: textClass,
			children: [
				table.getFilteredSelectedRowModel().rows.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [table.getFilteredSelectedRowModel().rows.length, " of "] }),
				rowCount,
				" ",
				countLabel
			]
		}), /* @__PURE__ */ jsxs("div", {
			className: cn("flex items-center", bar ? "gap-md" : "gap-lg"),
			children: [/* @__PURE__ */ jsxs("div", {
				className: cn("items-center gap-xs", bar ? "flex" : "hidden sm:flex"),
				children: [/* @__PURE__ */ jsx("span", {
					className: cn(textClass, bar && "hidden sm:inline"),
					children: "Rows per page"
				}), /* @__PURE__ */ jsx(Select, {
					className: "mb-0",
					triggerClassName: cn("w-auto min-w-fit shadow-none", bar && "h-7"),
					contentClassName: cn("w-fit min-w-[var(--anchor-width)]", !bar && "translate-x-[calc((var(--anchor-width)-100%)/2)]"),
					options: selectOptions,
					value: String(pageSize),
					onValueChange: (v) => table.setPageSize(Number(v)),
					size: "base"
				})]
			}), /* @__PURE__ */ jsxs("div", {
				className: cn("flex items-center", bar ? "gap-md" : "gap-xs"),
				children: [/* @__PURE__ */ jsxs("span", {
					className: textClass,
					children: [
						"Page ",
						pageIndex + 1,
						" of ",
						table.getPageCount() || 1
					]
				}), /* @__PURE__ */ jsxs("div", {
					className: "flex items-center gap-xxs",
					children: [
						navButton("First page", !table.getCanPreviousPage(), () => table.firstPage(), /* @__PURE__ */ jsx(CaretDoubleLeftIcon, {})),
						navButton("Previous page", !table.getCanPreviousPage(), () => table.previousPage(), /* @__PURE__ */ jsx(CaretLeftIcon, {})),
						navButton("Next page", !table.getCanNextPage(), () => table.nextPage(), /* @__PURE__ */ jsx(CaretRightIcon, {})),
						showLastPageButton && navButton("Last page", !table.getCanNextPage(), () => table.lastPage(), /* @__PURE__ */ jsx(CaretDoubleRightIcon, {}))
					]
				})]
			})]
		})]
	});
}
//#endregion
export { DataTable, DataTableColumnHeader, DataTablePagination, DataTableSubRowPanel, createSelectionColumn };

//# sourceMappingURL=DataTable.js.map